import React, { useState } from 'react';
import { Alert, Button, Divider, Modal, Space, Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { withTransportReturnContext } from '../../transports/transportReturnContext';
import TransportAttachCandidates from '../../transports/components/TransportAttachCandidates';
import type { TransportAttachResult } from '../../transports/api/transportRoutesApi';
import type { TransportKind } from '../../transports/transportDirection';

const { Text } = Typography;

interface OrderTransportLinkModalProps {
  open: boolean;
  orderId: string;
  /** Operator-facing ZK number for the success message. */
  orderLabel?: string;
  /** ZK unloading place („dojdzie nowy punkt rozładunku”). */
  deliveryPlace?: string | null;
  /** WP14: the customer collects himself — explain and offer the switch. */
  customerPickup?: boolean;
  onSwitchToOurDelivery?: () => Promise<boolean> | boolean;
  canViewCosts?: boolean;
  onAttached?: (result: TransportAttachResult) => void;
  onClose: () => void;
}

/**
 * „Powiąż transport” on the ZK card (decision 2026-10-04, F2): plan a new
 * truck with the kind preset („Magazyn → klient” by default, „Fabryka →
 * klient” on request) or add this ZK to an existing truck with one click.
 */
const OrderTransportLinkModal: React.FC<OrderTransportLinkModalProps> = ({
  open,
  orderId,
  orderLabel,
  deliveryPlace = null,
  customerPickup = false,
  onSwitchToOurDelivery,
  canViewCosts = false,
  onAttached,
  onClose,
}) => {
  const { t } = useTranslation(['transport', 'common']);
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  const openNewTransport = (kind: TransportKind) => {
    onClose();
    navigate(withTransportReturnContext(
      `/app/transports/new?orderId=${encodeURIComponent(orderId)}&kind=${kind}`,
      orderId,
    ));
  };

  const openExistingTransport = (transportId: string) => {
    onClose();
    navigate(
      withTransportReturnContext(`/app/transports/${encodeURIComponent(transportId)}/edit?orderId=${encodeURIComponent(orderId)}`, orderId),
    );
  };

  const switchDelivery = async () => {
    if (!onSwitchToOurDelivery) return;
    setSwitching(true);
    try {
      await onSwitchToOurDelivery();
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Modal
      title={t('transport:orderTransports.linkDialogTitle')}
      open={open}
      onCancel={onClose}
      width={760}
      footer={<Button onClick={onClose}>{t('common:close')}</Button>}
      destroyOnHidden
    >
      {customerPickup ? (
        <Alert
          type="warning"
          showIcon
          data-testid="order-transport-pickup-blocked"
          message={t('transport:attach.pickupTitle')}
          description={(
            <Space direction="vertical" size={8}>
              <Text>{t('transport:attach.pickupReason')}</Text>
              {onSwitchToOurDelivery ? (
                <Button
                  type="primary"
                  loading={switching}
                  onClick={() => { void switchDelivery(); }}
                  data-testid="order-transport-switch-delivery"
                >
                  {t('transport:attach.pickupSwitch')}
                </Button>
              ) : null}
            </Space>
          )}
        />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Typography.Title level={5} style={{ marginBottom: 8 }}>{t('transport:plan.newTruckTitle')}</Typography.Title>
            <Space wrap>
              <Button
                type="primary"
                icon={<ExportOutlined />}
                onClick={() => openNewTransport('WAREHOUSE_TO_CUSTOMER')}
                data-testid="order-transport-new-WAREHOUSE_TO_CUSTOMER"
              >
                {t('transport:plan.newWarehouseToCustomer')}
              </Button>
            </Space>
            {/* Owner 2026-10-10: a factory truck starts from the ZD; this ZK is
                named there in „Dla kogo jest ten towar?”. */}
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }} data-testid="order-transport-factory-hint">
              {t('transport:plan.factoryFromZdHint')}
            </Text>
          </div>
          <Divider style={{ margin: '4px 0' }} />
          <Typography.Title level={5} style={{ margin: 0 }}>{t('transport:attach.title')}</Typography.Title>
          <TransportAttachCandidates
            target={{ type: 'order', id: orderId, label: orderLabel || '', place: deliveryPlace }}
            canViewCosts={canViewCosts}
            onEditInstead={openExistingTransport}
            onAttached={(result) => {
              onAttached?.(result);
              onClose();
            }}
          />
        </Space>
      )}
    </Modal>
  );
};

export default OrderTransportLinkModal;
